/**
 * Sawyer answers 10-29, item 2 — "Going home today" on the Front Desk.
 *
 * Every dog with a checkout today, grouped by pick-up time, one row per dog:
 * the dog, owner, service, pick-up time, what it brought (and its locker),
 * and whether food goes home with it. The list is built once
 * (`buildGoingHomeList`) from the page's own `/today` payload, so the screen
 * and the printed sheet (GoingHomeBoard) can never disagree (Principle 1).
 *
 * Design-Philosophy principles served:
 *  - #1 One glance: the dog's name is the hero; "Send food home" is the only
 *    loud chip on a row, because forgetting the food is the failure Sawyer
 *    named.
 *  - #5 Never lie about data: loading, error + retry, an honest empty, the
 *    structural-zero notice (`dataSuspect`), a refresh failure over the last
 *    good list, and the deploy window (an older server) all read differently.
 *    "Couldn't load" is never drawn as "no belongings".
 *  - #2 The next action is obvious: the right edge of every row answers "can
 *    this dog leave?". Check Out (Sawyer, 10-30: "I didn't see a check out
 *    button on the going home section either"), who it leaves with, or not
 *    checked in yet. Check Out opens the visit's Bookings row (`onCheckOut`)
 *    and never posts, so every money gate stays where it is. The printed
 *    sheet keeps its tick box.
 *  - #7 Built for hands: the retry target and Check Out are ≥44px; rows stack
 *    at 375, and Check Out wraps under the dog when the row runs out of width.
 */
import type { DashboardTodayResponse } from '../dashboard/dashboard-api.js';
import { joinDogNames } from '../dashboard/household-checkin.js';
import {
  belongingsLabel,
  buildGoingHomeList,
  foodLabel,
  pickUpLabel,
  type GoingHomeCheckOut,
  type GoingHomeDogRow,
  type GoingHomeList,
} from './going-home-list.js';
import './going-home.css';

export interface GoingHomeTodayProps {
  readonly data: DashboardTodayResponse | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly onRetry: () => void;
  /** The server says today's zeros may be structural (stale or absent data). */
  readonly dataSuspect: boolean;
  /** Take the desk to this visit's check-out. The page owns where that is. */
  readonly onCheckOut: (bookingId: string) => void;
}

function CheckOutSlot({
  rowKey,
  bookingId,
  checkOut,
  onCheckOut,
}: {
  rowKey: string;
  bookingId: string;
  checkOut: GoingHomeCheckOut;
  onCheckOut: (bookingId: string) => void;
}): JSX.Element | null {
  switch (checkOut.kind) {
    case 'ready':
      return (
        // The Check In button's own classes (#1350): the tap height, the
        // brand color and its AA pairing come from one rule.
        <button
          type="button"
          className="ferro-arrivals__action ferro-arrivals__action--in ferro-gohome__check-out"
          onClick={() => onCheckOut(bookingId)}
          data-testid={`going-home-check-out-${rowKey}`}
          aria-label={`Check out ${joinDogNames(checkOut.dogNames)}`}
        >
          Check Out
        </button>
      );
    case 'with_visit':
      return (
        <span className="ferro-gohome__rides" data-testid={`going-home-rides-${rowKey}`}>
          Checks out with {checkOut.leadDogName}
        </span>
      );
    case 'not_checked_in':
      return (
        <span className="ferro-gohome__rides" data-testid={`going-home-not-in-${rowKey}`}>
          Not checked in yet
        </span>
      );
    case 'left':
      return null;
  }
}

function DogRow({
  row,
  onCheckOut,
}: {
  row: GoingHomeDogRow;
  onCheckOut: (bookingId: string) => void;
}): JSX.Element {
  const time = pickUpLabel(row.pickUpTime);
  const belongings = belongingsLabel(row);
  const food = foodLabel(row);
  return (
    <li className="ferro-gohome__row" data-testid={`going-home-row-${row.key}`}>
      <div className="ferro-gohome__facts">
        <div className="ferro-gohome__ident">
          <p className="ferro-gohome__dog">{row.dogName}</p>
          <p className="ferro-gohome__meta">
            {row.serviceLabel}
            {row.addedServices.length > 0 ? ` + ${row.addedServices.join(', ')}` : ''}
            {' · '}
            <span className="ferro-gohome__time">{time ? `Pick-up ${time}` : 'No pick-up time'}</span>
          </p>
        </div>
        {belongings !== null ? (
          <p className="ferro-gohome__belongings" data-testid={`going-home-belongings-${row.key}`}>
            <span className="ferro-gohome__label">Belongings</span> {belongings}
          </p>
        ) : null}
        {row.care !== 'not_sent' || food !== null ? (
          <div className="ferro-gohome__chips">
            {row.locker ? (
              <span className="ferro-gohome__chip ferro-gohome__chip--locker" data-testid={`going-home-locker-${row.key}`}>
                Locker {row.locker}
              </span>
            ) : null}
            {food ? (
              <span
                className={`ferro-gohome__chip ferro-gohome__chip--${food.tone}`}
                data-testid={`going-home-food-${row.key}`}
              >
                {food.text}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      <CheckOutSlot rowKey={row.key} bookingId={row.bookingId} checkOut={row.checkOut} onCheckOut={onCheckOut} />
    </li>
  );
}

export function GoingHomeListView({
  list,
  onCheckOut,
}: {
  list: GoingHomeList;
  onCheckOut: (bookingId: string) => void;
}): JSX.Element {
  return (
    <div className="ferro-gohome__blocks">
      {list.blocks.map((block) => (
        <section key={block.key} className="ferro-gohome__block" data-testid={`going-home-block-${block.key}`}>
          <h3 className="ferro-gohome__block-title">{block.label}</h3>
          <ul className="ferro-gohome__households">
            {block.households.map((h) => (
              <li key={h.key} className="ferro-gohome__household" data-testid={`going-home-household-${h.key}`}>
                <p className="ferro-gohome__owner">{h.customerName}</p>
                <ul className="ferro-gohome__rows">
                  {h.rows.map((row) => (
                    <DogRow key={row.key} row={row} onCheckOut={onCheckOut} />
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** The honesty notes above the list; shared with the printed sheet. */
export function GoingHomeNotes({
  list,
  dataSuspect,
}: {
  list: GoingHomeList;
  dataSuspect: boolean;
}): JSX.Element | null {
  const notes: Array<{ key: string; text: string }> = [];
  if (dataSuspect) {
    notes.push({
      key: 'suspect',
      text: "Heads up: today's schedule may be incomplete in this system. Check the previous system before relying on this list.",
    });
  }
  if (list.mode === 'checked_in_only' && list.dogCount > 0) {
    notes.push({
      key: 'checked-in-only',
      text: "Showing dogs already checked in. Dogs arriving today aren't on this list yet.",
    });
  }
  if (list.dogCount > 0 && !list.careSent) {
    notes.push({ key: 'no-care', text: "Belongings, lockers and food can't be shown yet." });
  }
  if (list.houseFoodNotRecorded) {
    notes.push({
      key: 'house-food',
      text: "\"Check for food\": the dog ate here, but House Food isn't set up as a meal charge in Settings, so we can't tell whose food it was.",
    });
  }
  if (notes.length === 0) return null;
  return (
    <div className="ferro-gohome__notes">
      {notes.map((n) => (
        <p key={n.key} className="ferro-gohome__note" data-testid={`going-home-note-${n.key}`}>
          {n.text}
        </p>
      ))}
    </div>
  );
}

export function GoingHomeToday({
  data,
  isLoading,
  error,
  onRetry,
  dataSuspect,
  onCheckOut,
}: GoingHomeTodayProps): JSX.Element {
  const list = data ? buildGoingHomeList({ arrivals: data.arrivals, departures: data.departures }) : null;
  return (
    <section className="ferro-gohome" data-testid="going-home-today" aria-labelledby="going-home-today-title">
      <h2 className="ferro-frontdesk__section-title" id="going-home-today-title">
        Going home today
        {list ? (
          <span className="ferro-frontdesk__count" data-testid="going-home-count">
            {list.dogCount}
          </span>
        ) : null}
      </h2>

      {!data && isLoading ? (
        <div className="ferro-frontdesk__skeleton" data-testid="going-home-skeleton" aria-busy="true">
          <div className="ferro-frontdesk__skeleton-row" />
          <div className="ferro-frontdesk__skeleton-row" />
        </div>
      ) : null}

      {error ? (
        <div className="ferro-frontdesk__error" role="alert" data-testid="going-home-error">
          <span>
            {data
              ? "Couldn't refresh who's going home. Showing the last list."
              : "Couldn't load who's going home today."}
          </span>
          <button type="button" className="ferro-btn ferro-btn--ghost ferro-gohome__retry" onClick={onRetry}>
            Retry
          </button>
        </div>
      ) : null}

      {list ? (
        <>
          <GoingHomeNotes list={list} dataSuspect={dataSuspect} />
          {list.dogCount === 0 ? (
            <p className="ferro-frontdesk__empty" data-testid="going-home-empty">
              {dataSuspect
                ? "Today's schedule isn't in this system yet, so no dogs show as going home."
                : 'No dogs going home today.'}
            </p>
          ) : (
            <GoingHomeListView list={list} onCheckOut={onCheckOut} />
          )}
        </>
      ) : null}
    </section>
  );
}
